/**
 * Inline (SRP) sign-in support for the Cognito/`oidc` provider.
 *
 * The hosted-UI redirect flow ends with oidc-client-ts holding a `User` in its store. When a
 * user signs in inline instead — email + password via SRP, using `@bb/auth-core` — we end up
 * with raw Cognito tokens and no oidc-client-ts session. Rather than build a parallel session
 * mode (which would duplicate ~1000 lines of refresh / leader-election / cross-tab machinery),
 * we construct the same `User` the redirect callback would have produced and store it under the
 * same key. Every existing consumer — useOIDCSession, bbAxios, getAuthStateSync, the route
 * beforeLoad guards — then works unchanged, because none of them care HOW the user got there.
 *
 * Proven by apps/bbcedit/src/auth/inlineSessionInjection.spike.test.tsx before this was written.
 *
 * NOTE ON THE RELOAD: we deliberately do not try to notify an already-mounted AuthProvider.
 * react-oidc-context builds its own UserManager internally, so `events.load()` on a second
 * instance would not reach it. The caller navigates after this resolves, and a fresh
 * AuthProvider picks the stored user up on mount — which is exactly what the redirect flow
 * does anyway (it is a full page navigation). Same UX, far less surface area.
 */
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

/** Scope string must match initOIDCAuthProviderConfig, or silent renew asks for different scopes. */
const OIDC_SCOPE = 'aws.cognito.signin.user.admin email openid profile';

/**
 * Cognito's `authority` is `https://cognito-idp.<region>.amazonaws.com/<userPoolId>`, and the
 * SRP client needs the bare pool id. Deriving it here keeps callers from re-parsing the URL.
 *
 * @param {string} authority
 * @returns {string|null} the user pool id, or null if the authority is not the expected shape
 */
export function userPoolIdFromAuthority(authority) {
  if (!authority) return null;
  const last = String(authority).split('/').filter(Boolean).pop();
  // Pool ids look like us-east-2_qtf9DQOsM — region prefix, underscore, opaque suffix.
  return /^[a-z]{2}-[a-z]+-\d+_[A-Za-z0-9]+$/.test(last || '') ? last : null;
}

/**
 * Decode a JWT payload without verifying it. The token was just issued to us by Cognito over
 * TLS via the SRP handshake, so this is only to populate `profile` for UI use — never a
 * trust decision. The API verifies signatures server-side.
 *
 * @param {string} jwt
 * @returns {Object} decoded claims, or {} when the token is not decodable
 */
function decodeJwtPayload(jwt) {
  try {
    const payload = String(jwt).split('.')[1];
    if (!payload) return {};
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

/**
 * Store SRP-issued tokens as an oidc-client-ts session.
 *
 * @param {Object} params
 * @param {{accessToken: string, idToken: string, refreshToken: string, expiresIn: number}} params.tokens
 *        exactly what `@bb/auth-core` signInWithPassword resolves with
 * @param {{authority: string, clientId: string}} params.oidcAuth  the app's config.oidcAuth
 * @returns {Promise<void>} resolves once the user is persisted
 */
export async function signInInline({ tokens, oidcAuth }) {
  if (!tokens?.accessToken || !tokens?.idToken) {
    throw new Error('signInInline: missing tokens');
  }
  const { authority, clientId } = oidcAuth || {};
  if (!authority || !clientId) {
    throw new Error('signInInline: oidcAuth.authority and oidcAuth.clientId are required');
  }

  const userManager = new UserManager({
    authority,
    client_id: clientId,
    // redirect_uri is required by UserManagerSettings but unused on this path — no redirect
    // happens. Kept identical to the hosted-UI config so both write the same store key.
    redirect_uri: `${window.location.origin}/bbweb/home`,
    response_type: 'code',
    scope: OIDC_SCOPE,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: false,
  });

  const claims = decodeJwtPayload(tokens.idToken);
  const user = new User({
    access_token: tokens.accessToken,
    id_token: tokens.idToken,
    refresh_token: tokens.refreshToken,
    token_type: 'Bearer',
    scope: OIDC_SCOPE,
    expires_at: Math.floor(Date.now() / 1000) + (tokens.expiresIn || 0),
    profile: {
      // oidc-client-ts requires iss/aud/sub/exp/iat to consider the profile well-formed.
      iss: claims.iss || authority,
      aud: claims.aud || clientId,
      sub: claims.sub,
      exp: claims.exp,
      iat: claims.iat,
      ...claims,
    },
  });

  await userManager.storeUser(user);
}
